import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import type { ReactNode } from "react";

export interface InboxChat {
  channel_name: string;
  shipment_id: number;
  shipment_label: string;
  unread_count: number;
  latest_message_preview: string;
  latest_message_sender_name: string;
  latest_message_time_token: string;
  counterparty_company_name: string | null;
}

interface ChatUnreadContextState {
  inbox: ReadonlyMap<string, InboxChat>;
  inboxArray: InboxChat[];
  hasAnyUnread: boolean;
  unreadCount: number;
  truncated: boolean;
  seed: (rows: InboxChat[], truncated: boolean) => void;
  applyIncomingMessage: (incoming: InboxChat) => void;
  markChannelRead: (channelName: string) => void;
}

const ChatUnreadContext = createContext<ChatUnreadContextState | undefined>(
  undefined,
);

interface ChatUnreadProviderProps {
  children: ReactNode;
}

function compareTimeTokenDesc(a: InboxChat, b: InboxChat): number {
  // PubNub time tokens are 17-digit numeric strings; lexical comparison is
  // safe because they are all the same length and zero-padded by PubNub.
  if (a.latest_message_time_token === b.latest_message_time_token) return 0;
  return a.latest_message_time_token > b.latest_message_time_token ? -1 : 1;
}

function compareInboxRows(a: InboxChat, b: InboxChat): number {
  const aIsUnread = a.unread_count > 0 ? 0 : 1;
  const bIsUnread = b.unread_count > 0 ? 0 : 1;
  if (aIsUnread !== bIsUnread) return aIsUnread - bIsUnread;
  return compareTimeTokenDesc(a, b);
}

export function ChatUnreadProvider(props: ChatUnreadProviderProps) {
  const { children } = props;
  const [inbox, setInbox] = useState<Map<string, InboxChat>>(new Map());
  const [truncated, setTruncated] = useState<boolean>(false);

  const seed = useCallback((rows: InboxChat[], nextTruncated: boolean) => {
    const next = new Map<string, InboxChat>();
    rows.forEach((row) => next.set(row.channel_name, row));
    setInbox(next);
    setTruncated(nextTruncated);
  }, []);

  const applyIncomingMessage = useCallback((incoming: InboxChat) => {
    setInbox((prev) => {
      const next = new Map(prev);
      const existing = prev.get(incoming.channel_name);
      next.set(incoming.channel_name, {
        ...incoming,
        unread_count: (existing?.unread_count ?? 0) + 1,
        // Realtime envelopes don't carry counterparty/custom_name; keep the
        // seeded label when one is available so the row title doesn't
        // regress to the bare "Shipment #N" form on the next message.
        shipment_label: existing?.shipment_label ?? incoming.shipment_label,
        counterparty_company_name:
          incoming.counterparty_company_name ??
          existing?.counterparty_company_name ??
          null,
      });
      return next;
    });
  }, []);

  const markChannelRead = useCallback((channelName: string) => {
    setInbox((prev) => {
      const existing = prev.get(channelName);
      if (existing === undefined || existing.unread_count === 0) return prev;
      const next = new Map(prev);
      next.set(channelName, { ...existing, unread_count: 0 });
      return next;
    });
  }, []);

  const value = useMemo<ChatUnreadContextState>(() => {
    const inboxArray = Array.from(inbox.values()).sort(compareInboxRows);
    const unreadCount = inboxArray.reduce(
      (acc, row) => (row.unread_count > 0 ? acc + 1 : acc),
      0,
    );
    return {
      inbox,
      inboxArray,
      hasAnyUnread: unreadCount > 0,
      unreadCount,
      truncated,
      seed,
      applyIncomingMessage,
      markChannelRead,
    };
  }, [inbox, truncated, seed, applyIncomingMessage, markChannelRead]);

  return (
    <ChatUnreadContext.Provider value={value}>
      {children}
    </ChatUnreadContext.Provider>
  );
}

export function useChatUnreadContext(): ChatUnreadContextState {
  const context = useContext(ChatUnreadContext);
  if (context === undefined) {
    throw new Error(
      "useChatUnreadContext must be used within a ChatUnreadProvider",
    );
  }
  return context;
}
